import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    TextField,
    Grid,
    MenuItem,
    Autocomplete,
    Stack
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    MenuBook as DocIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchFactures } from '../../data/slices/factureSlice';
import { fetchClients } from '../../data/slices/clientStore';
import { fetchPaiments } from '../../data/slices/paiementsSlice';
import PageHeader from '../../components/layout/PageHeader';
import {
    generateExtraitCompte,
    generateReleveCompte,
    generateBalanceGenerale,
    generateBalanceAgee
} from '../../utils/compteClientEngine';
import ExtraitCompteViewer from './components/ExtraitCompteViewer';
import ReleveCompteViewer from './components/ReleveCompteViewer';
import BalanceGeneraleViewer from './components/BalanceGeneraleViewer';
import BalanceAgeeViewer from './components/BalanceAgeeViewer';
import dayjs from 'dayjs';

const DOC_TYPES = [
    { value: 'EXTRAIT', label: 'Extrait de compte (Mouvements chronologiques)' },
    { value: 'RELEVE', label: 'Relevé de compte (Factures ouvertes à date)' },
    { value: 'BALANCE_GENERALE', label: 'Balance générale clients (Situation globale)' },
    { value: 'BALANCE_AGEE', label: 'Balance âgée clients (Retards par tranche)' }
];

const CompteClientPage = () => {
    const dispatch = useDispatch();
    const { factures } = useSelector((state) => state.factures);
    const { clients } = useSelector((state) => state.clients);
    const { paiements } = useSelector((state) => state.paiements);

    // Paramètres de sélection (Section 5.2 & 5.6)
    const [selectedClient, setSelectedClient] = useState(null);
    const [docType, setDocType] = useState('EXTRAIT');
    const [dateDebut, setDateDebut] = useState(dayjs().startOf('year').format('YYYY-MM-DD'));
    const [dateFin, setDateFin] = useState(dayjs().format('YYYY-MM-DD'));
    const [dateSituation, setDateSituation] = useState(dayjs().format('YYYY-MM-DD'));

    // Filtre interne du relevé
    const [releveStatusFilter, setReleveStatusFilter] = useState('ALL');

    // Mémorisation de l'historique des encaissements locaux
    const encaissementsEnregistres = useMemo(() => {
        try {
            const saved = localStorage.getItem('aft_historique_encaissements');
            const localList = saved ? JSON.parse(saved) : [];
            const reduxList = Array.isArray(paiements) ? paiements : [];
            return [...localList, ...reduxList];
        } catch {
            return Array.isArray(paiements) ? paiements : [];
        }
    }, [paiements]);

    const location = useLocation();

    useEffect(() => {
        dispatch(fetchFactures());
        dispatch(fetchClients());
        dispatch(fetchPaiments());
    }, [dispatch]);

    useEffect(() => {
        if (location.state?.client && Array.isArray(clients) && clients.length > 0) {
            const passed = location.state.client;
            const found = clients.find(c =>
                (passed.id && (c.id === passed.id || c._id === passed.id)) ||
                (passed.code && c.code === passed.code) ||
                (passed.nomComplet && c.nomComplet === passed.nomComplet) ||
                (passed.libelle && c.libelle === passed.libelle)
            );
            if (found) {
                setSelectedClient(found);
            } else if (typeof passed === 'object') {
                setSelectedClient(passed);
            }
        }
    }, [location.state, clients]);

    // Sélection automatique du premier client si aucun sélectionné pour Extrait ou Relevé
    useEffect(() => {
        if (!selectedClient && !location.state?.client && Array.isArray(clients) && clients.length > 0) {
            setSelectedClient(clients[0]);
        }
    }, [clients, selectedClient, location.state]);

    // RÈGLE SECTION 5.6 : Adaptation des champs selon le type de document
    const isExtrait = docType === 'EXTRAIT';
    const isMultiClient = docType === 'BALANCE_GENERALE' || docType === 'BALANCE_AGEE';

    // Calculs des documents
    const extraitData = useMemo(() => {
        if (!isExtrait || !selectedClient) return null;
        return generateExtraitCompte(selectedClient, factures, encaissementsEnregistres, dateDebut, dateFin);
    }, [isExtrait, selectedClient, factures, encaissementsEnregistres, dateDebut, dateFin]);

    const releveData = useMemo(() => {
        if (docType !== 'RELEVE' || !selectedClient) return null;
        return generateReleveCompte(selectedClient, factures, encaissementsEnregistres, dateSituation, releveStatusFilter);
    }, [docType, selectedClient, factures, encaissementsEnregistres, dateSituation, releveStatusFilter]);

    const balanceGeneraleData = useMemo(() => {
        if (docType !== 'BALANCE_GENERALE') return null;
        return generateBalanceGenerale(clients, factures, encaissementsEnregistres, dateSituation);
    }, [docType, clients, factures, encaissementsEnregistres, dateSituation]);

    const balanceAgeeData = useMemo(() => {
        if (docType !== 'BALANCE_AGEE') return null;
        return generateBalanceAgee(clients, factures, encaissementsEnregistres, dateSituation);
    }, [docType, clients, factures, encaissementsEnregistres, dateSituation]);

    const handleSelectClientFromBalance = (clientRow) => {
        const found = (clients || []).find(c => String(c.id) === String(clientRow.id) || String(c.code) === String(clientRow.id) || c.libelle === clientRow.nom);
        if (found) {
            setSelectedClient(found);
            setDocType('RELEVE');
        }
    };

    const handleReset = () => {
        setDocType('EXTRAIT');
        setDateDebut(dayjs().startOf('year').format('YYYY-MM-DD'));
        setDateFin(dayjs().format('YYYY-MM-DD'));
        setDateSituation(dayjs().format('YYYY-MM-DD'));
        setReleveStatusFilter('ALL');
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader
                title="Compte client"
                subtitle="Édition des extraits, relevés de compte et balances clients (générale & âgée)"
                icon={DocIcon}
                breadcrumbs={[
                    { label: 'Facturation', path: '/facturation/gestion-client' },
                    { label: 'Compte client', path: '/facturation/compte-client' }
                ]}
            />

            {/* ÉCRAN DE SÉLECTION ADAPTATIF (SECTION 5.2 & 5.6) */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Compte client — Critères de sélection
                </Typography>

                <Grid container spacing={2.5} alignItems="center">
                    {/* Type de document */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Type de document"
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                        >
                            {DOC_TYPES.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                        </TextField>
                    </Grid>

                    {/* Client (actif si Extrait ou Relevé, optionnel si Balance) */}
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={Array.isArray(clients) ? clients : []}
                            getOptionLabel={(option) => {
                                if (!option) return '';
                                const name = option.libelle || option.nomComplet || option.nom || option.code || '';
                                const code = option.code ? ` (${option.code})` : '';
                                return `${name}${code}`;
                            }}
                            isOptionEqualToValue={(option, val) => (option?.id && val?.id ? option.id === val.id : option === val)}
                            renderOption={(props, option, { index }) => {
                                const { key, ...otherProps } = props;
                                const uniqueKey = option.id || option._id || (option.code ? `cc-client-code-${option.code}-${index}` : `cc-client-idx-${index}`);
                                const name = option.libelle || option.nomComplet || option.nom || option.code || 'Client';
                                const phone = option.telephone ? ` — ${option.telephone}` : '';
                                const code = option.code ? ` [${option.code}]` : '';
                                return (
                                    <li key={uniqueKey} {...otherProps}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                                {name}{code}
                                            </Typography>
                                            {phone && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {phone}
                                                </Typography>
                                            )}
                                        </Box>
                                    </li>
                                );
                            }}
                            value={selectedClient}
                            disabled={isMultiClient}
                            onChange={(e, val) => setSelectedClient(val)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label={isMultiClient ? "Client (Tous les clients pour la balance)" : "Client (Recherche dynamique)"}
                                    placeholder="Sélectionner le client..."
                                />
                            )}
                        />
                    </Grid>

                    {/* Période De ... À ... (ACTIVÉ si Extrait, GRISÉ si Relevé ou Balance - Règle 5.6) */}
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            label="Période : De"
                            InputLabelProps={{ shrink: true }}
                            value={dateDebut}
                            disabled={!isExtrait}
                            onChange={(e) => setDateDebut(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            label="Période : À"
                            InputLabelProps={{ shrink: true }}
                            value={dateFin}
                            disabled={!isExtrait}
                            onChange={(e) => setDateFin(e.target.value)}
                        />
                    </Grid>

                    {/* Date de situation (ACTIVÉ si Relevé ou Balance, GRISÉ si Extrait - Règle 5.6) */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            label="Date de situation"
                            InputLabelProps={{ shrink: true }}
                            value={dateSituation}
                            disabled={isExtrait}
                            onChange={(e) => setDateSituation(e.target.value)}
                            helperText={isExtrait ? "Inaccessible en mode Extrait" : "Situation à date"}
                        />
                    </Grid>

                    {/* Boutons d'action */}
                    <Grid item xs={12} sm={6} md={9}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleReset}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Réinitialiser
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Afficher le document
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* RENDU DU DOCUMENT SÉLECTIONNÉ */}
            <Box sx={{ mt: 3 }}>
                {docType === 'EXTRAIT' && <ExtraitCompteViewer data={extraitData} />}
                
                {docType === 'RELEVE' && (
                    <ReleveCompteViewer
                        data={releveData}
                        statusFilter={releveStatusFilter}
                        onChangeStatusFilter={setReleveStatusFilter}
                    />
                )}

                {docType === 'BALANCE_GENERALE' && (
                    <BalanceGeneraleViewer
                        data={balanceGeneraleData}
                        onSelectClient={handleSelectClientFromBalance}
                    />
                )}

                {docType === 'BALANCE_AGEE' && (
                    <BalanceAgeeViewer
                        data={balanceAgeeData}
                        onSelectClient={handleSelectClientFromBalance}
                    />
                )}
            </Box>
        </Box>
    );
};

export default CompteClientPage;
